import { useState, useEffect } from "react";
import { AdminLayout } from "./AdminDashboard";
import API from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { TrendingUp, Save, DollarSign } from "lucide-react";

export default function AdminEconomy() {
  const [settings, setSettings] = useState({});
  const [resources, setResources] = useState([]);
  const [form, setForm] = useState({ conversion_rate: "", selling_tax: "", min_withdrawal: "", max_withdrawal: "" });
  const [prices, setPrices] = useState({});
  const [loading, setLoading] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await API.get("/admin/economy");
        setSettings(data.settings);
        setResources(data.resources);
        setForm({
          conversion_rate: data.settings.conversion_rate || 100,
          selling_fee: (data.settings.selling_fee || data.settings.selling_tax || 0.05) * 100,
          min_withdrawal: data.settings.min_withdrawal || 1,
          max_withdrawal: data.settings.max_withdrawal || 1000,
          min_deposit: data.settings.min_deposit || 0.01,
          max_deposit: data.settings.max_deposit || 10000,
          daily_withdrawal_limit: data.settings.daily_withdrawal_limit || 500,
          withdrawal_fee: (data.settings.withdrawal_fee || 0.05) * 100,
          supported_cryptos: (data.settings.supported_cryptos || ["BTC","LTC","DOGE","TRX","ETH","USDT"]).join(","),
          faucetpay_deposit_rate: data.settings.faucetpay_deposit_rate || 100,
          collect_cooldown_minutes: data.settings.collect_cooldown_minutes || 5,
          faucetpay_deposit_mode: data.settings.faucetpay_deposit_mode || "semi_auto",
          deposit_rate_limit_per_hour: data.settings.deposit_rate_limit_per_hour || 5,
          crypto_rates: JSON.stringify(data.settings.crypto_rates || {}),
          deposit_wallet_addresses: JSON.stringify(data.settings.deposit_wallet_addresses || {}),
          deposit_networks: JSON.stringify(data.settings.deposit_networks || {}),
          deposit_warning_text: data.settings.deposit_warning_text || "Be sure to copy the address and send using the same network shown.",
          withdrawal_warning_text: data.settings.withdrawal_warning_text || "Be sure to send to the correct address and network. Withdrawals sent to wrong networks cannot be recovered.",
          auto_lock_suspicious: data.settings.auto_lock_suspicious || false,
          allow_withdraw_purchases: data.settings.allow_withdraw_purchases || false,
          withdraw_purchase_fee: (data.settings.withdraw_purchase_fee || 0.30) * 100,
          faucetpay_min_withdrawal: data.settings.faucetpay_min_withdrawal || 0.10,
          faucetpay_withdrawal_fee: (data.settings.faucetpay_withdrawal_fee || 0.02) * 100,
          faucetpay_withdrawal_text: data.settings.faucetpay_withdrawal_text || "Withdraw directly to your FaucetPay wallet. May be instant or pending admin approval depending on settings.",
        });
        const p = {};
        data.resources.forEach((r) => { p[r.name] = r.current_price; });
        setPrices(p);
      } catch {}
    };
    load();
  }, []);

  const saveSettings = async (e) => {
    e.preventDefault();
    setLoading("settings");
    try {
      await API.put("/admin/economy", {
        conversion_rate: parseFloat(form.conversion_rate),
        selling_fee: parseFloat(form.selling_fee) / 100,
        min_withdrawal: parseFloat(form.min_withdrawal),
        max_withdrawal: parseFloat(form.max_withdrawal),
        min_deposit: parseFloat(form.min_deposit),
        max_deposit: parseFloat(form.max_deposit),
        daily_withdrawal_limit: parseFloat(form.daily_withdrawal_limit),
        withdrawal_fee: parseFloat(form.withdrawal_fee) / 100,
        supported_cryptos: form.supported_cryptos.split(",").map(s => s.trim()).filter(Boolean),
        faucetpay_deposit_rate: parseFloat(form.faucetpay_deposit_rate),
        collect_cooldown_minutes: parseFloat(form.collect_cooldown_minutes),
        faucetpay_deposit_mode: form.faucetpay_deposit_mode,
        deposit_rate_limit_per_hour: parseInt(form.deposit_rate_limit_per_hour),
        crypto_rates: JSON.parse(form.crypto_rates || "{}"),
        deposit_wallet_addresses: JSON.parse(form.deposit_wallet_addresses || "{}"),
        deposit_networks: JSON.parse(form.deposit_networks || "{}"),
        deposit_warning_text: form.deposit_warning_text,
        withdrawal_warning_text: form.withdrawal_warning_text,
        auto_lock_suspicious: form.auto_lock_suspicious,
        allow_withdraw_purchases: form.allow_withdraw_purchases,
        withdraw_purchase_fee: parseFloat(form.withdraw_purchase_fee) / 100,
        faucetpay_min_withdrawal: parseFloat(form.faucetpay_min_withdrawal),
        faucetpay_withdrawal_fee: parseFloat(form.faucetpay_withdrawal_fee) / 100,
        faucetpay_withdrawal_text: form.faucetpay_withdrawal_text,
      });
      toast.success("Economy settings updated");
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  const updatePrice = async (name) => {
    setLoading(`price-${name}`);
    try {
      await API.put(`/admin/resources/${name}/price`, { price: parseFloat(prices[name]) });
      toast.success(`${name} price updated`);
    } catch (e) { toast.error("Failed"); }
    finally { setLoading(""); }
  };

  return (
    <AdminLayout title="Economy Control">
      <div className="space-y-6" data-testid="admin-economy">
        {/* Economy Settings */}
        <form onSubmit={saveSettings} className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6 space-y-4" data-testid="economy-settings-form">
          <h3 className="text-lg font-bold text-[#F8FAFC] flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#FFD600]" /> Economy Settings</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#94A3B8] text-sm">Conversion Rate (coins per unit)</Label>
              <Input type="number" value={form.conversion_rate} onChange={(e) => setForm({ ...form, conversion_rate: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-conversion-rate" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Selling Fee (%)</Label>
              <Input type="number" step="0.1" value={form.selling_fee} onChange={(e) => setForm({ ...form, selling_fee: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-selling-fee" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Min Withdrawal</Label>
              <Input type="number" value={form.min_withdrawal} onChange={(e) => setForm({ ...form, min_withdrawal: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-min-withdrawal" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Max Withdrawal</Label>
              <Input type="number" value={form.max_withdrawal} onChange={(e) => setForm({ ...form, max_withdrawal: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-max-withdrawal" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#94A3B8] text-sm">Supported Cryptos (comma-separated)</Label>
              <Input value={form.supported_cryptos} onChange={(e) => setForm({ ...form, supported_cryptos: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-cryptos" placeholder="BTC,LTC,DOGE,TRX,ETH,USDT" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">FaucetPay Deposit Rate (sat/coin)</Label>
              <Input type="number" value={form.faucetpay_deposit_rate} onChange={(e) => setForm({ ...form, faucetpay_deposit_rate: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-fp-rate" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Collect Cooldown (minutes)</Label>
              <Input type="number" step="0.5" value={form.collect_cooldown_minutes} onChange={(e) => setForm({ ...form, collect_cooldown_minutes: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-cooldown" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Min Deposit (USD)</Label>
              <Input type="number" step="0.01" value={form.min_deposit} onChange={(e) => setForm({ ...form, min_deposit: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Max Deposit (USD)</Label>
              <Input type="number" value={form.max_deposit} onChange={(e) => setForm({ ...form, max_deposit: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Daily WD Limit (USD)</Label>
              <Input type="number" value={form.daily_withdrawal_limit} onChange={(e) => setForm({ ...form, daily_withdrawal_limit: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Withdrawal Fee (%)</Label>
              <Input type="number" step="0.1" value={form.withdrawal_fee} onChange={(e) => setForm({ ...form, withdrawal_fee: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">Deposit Rate Limit (/hour)</Label>
              <Input type="number" value={form.deposit_rate_limit_per_hour} onChange={(e) => setForm({ ...form, deposit_rate_limit_per_hour: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">FP Deposit Mode</Label>
              <select value={form.faucetpay_deposit_mode} onChange={(e) => setForm({ ...form, faucetpay_deposit_mode: e.target.value })}
                className="w-full h-10 mt-1 bg-[#0B0E14] border border-[#2A3241] text-[#F8FAFC] rounded-md px-3 text-sm">
                <option value="auto">Auto (instant credit)</option>
                <option value="semi_auto">Semi-Auto (admin confirms)</option>
                <option value="manual">Manual only</option>
              </select>
            </div>
            <div className="flex items-end">
              <button type="button" onClick={() => setForm({ ...form, auto_lock_suspicious: !form.auto_lock_suspicious })}
                className={`px-4 py-2.5 text-sm font-bold rounded-lg border ${form.auto_lock_suspicious ? "border-[#EF4444]/40 bg-[#EF4444]/10 text-[#EF4444]" : "border-[#2A3241] text-[#64748B]"}`}>
                Auto-Lock Suspicious: {form.auto_lock_suspicious ? "ON" : "OFF"}
              </button>
            </div>
            <div className="flex items-end">
              <button type="button" onClick={() => setForm({ ...form, allow_withdraw_purchases: !form.allow_withdraw_purchases })}
                className={`px-4 py-2.5 text-sm font-bold rounded-lg border ${form.allow_withdraw_purchases ? "border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#F59E0B]" : "border-[#2A3241] text-[#64748B]"}`}>
                WD Purchases: {form.allow_withdraw_purchases ? "ALLOWED" : "BLOCKED"}
              </button>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">WD Purchase Fee (%)</Label>
              <Input type="number" step="1" value={form.withdraw_purchase_fee} onChange={(e) => setForm({ ...form, withdraw_purchase_fee: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" />
              <p className="text-[9px] text-[#64748B] mt-0.5">Fee when buying coins with withdrawable balance (default 30%)</p>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">FaucetPay Min Withdrawal (USD)</Label>
              <Input type="number" step="0.01" value={form.faucetpay_min_withdrawal} onChange={(e) => setForm({ ...form, faucetpay_min_withdrawal: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-fp-min-wd" />
              <p className="text-[9px] text-[#64748B] mt-0.5">Minimum for FaucetPay withdrawals (default $0.10)</p>
            </div>
            <div>
              <Label className="text-[#94A3B8] text-sm">FaucetPay WD Fee (%)</Label>
              <Input type="number" step="0.1" value={form.faucetpay_withdrawal_fee} onChange={(e) => setForm({ ...form, faucetpay_withdrawal_fee: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-fp-wd-fee" />
              <p className="text-[9px] text-[#64748B] mt-0.5">Fee for FaucetPay withdrawals (default 2%)</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#94A3B8] text-sm">FaucetPay Withdrawal Description</Label>
              <Input value={form.faucetpay_withdrawal_text} onChange={(e) => setForm({ ...form, faucetpay_withdrawal_text: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-fp-wd-text" />
              <p className="text-[9px] text-[#64748B] mt-0.5">Shown to users on the FaucetPay withdrawal form</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#94A3B8] text-sm">Crypto Rates (JSON: {"{"}"BTC": 97000, ...{"}"})</Label>
              <Input value={form.crypto_rates} onChange={(e) => setForm({ ...form, crypto_rates: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 font-mono-game text-xs" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#94A3B8] text-sm">Deposit Wallet Addresses (JSON: {"{"}"BTC": "bc1...", ...{"}"})</Label>
              <Input value={form.deposit_wallet_addresses} onChange={(e) => setForm({ ...form, deposit_wallet_addresses: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 font-mono-game text-xs" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#94A3B8] text-sm">Deposit Networks (JSON: {"{"}"BTC": "Bitcoin Mainnet", "ETH": "ERC-20", ...{"}"})</Label>
              <Input value={form.deposit_networks} onChange={(e) => setForm({ ...form, deposit_networks: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1 font-mono-game text-xs" data-testid="economy-deposit-networks" />
              <p className="text-[9px] text-[#64748B] mt-0.5">Network name shown to users on each deposit address</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#94A3B8] text-sm">Deposit Warning Text</Label>
              <Input value={form.deposit_warning_text} onChange={(e) => setForm({ ...form, deposit_warning_text: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-deposit-warning" />
              <p className="text-[9px] text-[#64748B] mt-0.5">Warning shown below the deposit address (e.g., "Be sure to copy the address and send using the same network shown.")</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-[#94A3B8] text-sm">Withdrawal Warning Text</Label>
              <Input value={form.withdrawal_warning_text} onChange={(e) => setForm({ ...form, withdrawal_warning_text: e.target.value })}
                className="bg-[#0B0E14] border-[#2A3241] text-[#F8FAFC] mt-1" data-testid="economy-withdrawal-warning" />
              <p className="text-[9px] text-[#64748B] mt-0.5">Warning shown on the standard withdrawal form</p>
            </div>
          </div>
          <button type="submit" disabled={loading === "settings"} data-testid="save-economy-btn"
            className="flex items-center gap-2 px-6 py-3 text-sm font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-lg hover:bg-[#FFD600]/90 disabled:opacity-50">
            <Save className="w-4 h-4" /> {loading === "settings" ? "Saving..." : "Save Settings"}
          </button>
        </form>

        {/* Resource Prices */}
        <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-6" data-testid="resource-prices">
          <h3 className="text-lg font-bold text-[#F8FAFC] flex items-center gap-2 mb-4"><DollarSign className="w-5 h-5 text-[#10B981]" /> Resource Prices</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((r) => (
              <div key={r.name} className="bg-[#0B0E14] rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="text-sm font-medium text-[#F8FAFC]">{r.name}</p>
                    <p className="text-xs text-[#64748B] capitalize">{r.tier} &middot; Base: {r.base_price}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input type="number" step="0.1" value={prices[r.name] || ""} onChange={(e) => setPrices({ ...prices, [r.name]: e.target.value })}
                    className="bg-[#131722] border-[#2A3241] text-[#F8FAFC] h-9 text-sm" />
                  <button onClick={() => updatePrice(r.name)} disabled={loading === `price-${r.name}`} data-testid={`save-price-${r.name.toLowerCase()}`}
                    className="px-3 py-1 text-xs font-bold uppercase bg-[#10B981] text-white rounded-lg hover:bg-[#10B981]/90 disabled:opacity-50">
                    {loading === `price-${r.name}` ? "..." : "Set"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
